import {
  serviceTypeCreateSchema,
  type ServiceTypeDto,
} from '@kominiarz/shared';
import { ArrowDown, ArrowUp, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { Alert } from '../../components/Alert';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { SelectInput } from '../../components/form/SelectInput';
import { Switch } from '../../components/form/Switch';
import { TextInput } from '../../components/form/TextInput';
import { Spinner } from '../../components/Loading';
import { PageBody } from '../../components/PageBody';
import { PageHeader } from '../../components/PageHeader';
import { useToast } from '../../components/Toast';
import { ApiError, errorMessage } from '../../lib/api';
import { useCreateServiceType, useReorderServiceTypes, useServiceTypes, useUpdateServiceType } from '../../lib/visitsApi';
import styles from './settings.module.css';

const PERIOD_OPTIONS = [3, 6, 12, 24].map((m) => ({ value: String(m), label: `co ${m} miesięcy` }));

export function ServiceTypesPage() {
  const types = useServiceTypes(true);
  const reorder = useReorderServiceTypes();
  const toast = useToast();
  const [editing, setEditing] = useState<ServiceTypeDto | 'new' | null>(null);

  /** Swaps a type with its neighbour; the list order is the order in the visit form. */
  async function move(index: number, by: -1 | 1) {
    const ids = types.data!.map((t) => t.id);
    [ids[index], ids[index + by]] = [ids[index + by]!, ids[index]!];
    try {
      await reorder.mutateAsync(ids);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  return (
    <>
      <PageHeader
        title="Typy usług"
        back="/ustawienia"
        actions={
          <Button variant="primary" icon={<Plus aria-hidden="true" />} onClick={() => setEditing('new')}>
            Dodaj
          </Button>
        }
      />
      <PageBody>
        <p className={styles.intro}>
          Typy usług do wyboru przy wizycie, w tej kolejności co tutaj – strzałkami zmienisz kolejność. Dla usług
          okresowych aplikacja po wykonaniu wizyty zaproponuje termin kolejnego przeglądu.
        </p>
        {types.isPending && <Spinner />}
        {types.isError && <Alert kind="error">{errorMessage(types.error)}</Alert>}
        {types.data && (
          <ul className={styles.list}>
            {types.data.map((t, i) => (
              <li key={t.id} className={styles.row}>
                <span className={styles.rowMain}>
                  <span className={t.isActive ? styles.rowName : `${styles.rowName} ${styles.muted}`}>{t.name}</span>
                  <span className={styles.rowBadges}>
                    {t.isPeriodic && <Badge tone="info">Okresowa, co {t.periodMonths} mies.</Badge>}
                    {!t.isActive && <Badge tone="neutral">Ukryta</Badge>}
                  </span>
                </span>
                <span className={styles.order}>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<ArrowUp aria-hidden="true" />}
                    disabled={i === 0 || reorder.isPending}
                    onClick={() => move(i, -1)}
                  >
                    <span className="visually-hidden">Przesuń wyżej: {t.name}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<ArrowDown aria-hidden="true" />}
                    disabled={i === types.data.length - 1 || reorder.isPending}
                    onClick={() => move(i, 1)}
                  >
                    <span className="visually-hidden">Przesuń niżej: {t.name}</span>
                  </Button>
                </span>
                <Button variant="ghost" icon={<Pencil aria-hidden="true" />} onClick={() => setEditing(t)}>
                  Zmień
                </Button>
              </li>
            ))}
          </ul>
        )}
      </PageBody>
      {editing && <ServiceTypeDialog type={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function ServiceTypeDialog({ type, onClose }: { type: ServiceTypeDto | null; onClose: () => void }) {
  const toast = useToast();
  const create = useCreateServiceType();
  const update = useUpdateServiceType();
  const [name, setName] = useState(type?.name ?? '');
  const [isPeriodic, setPeriodic] = useState(type?.isPeriodic ?? false);
  const [periodMonths, setPeriod] = useState(type?.periodMonths ?? 12);
  const [isActive, setActive] = useState(type?.isActive ?? true);
  const [error, setError] = useState<string>();

  async function save() {
    const parsed = serviceTypeCreateSchema.safeParse({ name, isPeriodic, periodMonths });
    if (!parsed.success) {
      setError(parsed.error.issues[0]!.message);
      return;
    }
    try {
      if (type) await update.mutateAsync({ id: type.id, body: { ...parsed.data, isActive } });
      else await create.mutateAsync(parsed.data);
      toast.success('Zapisano typ usługi');
      onClose();
    } catch (err) {
      setError(err instanceof ApiError && err.fields.name ? err.fields.name : errorMessage(err));
    }
  }

  return (
    <Dialog
      open
      title={type ? 'Typ usługi' : 'Nowy typ usługi'}
      onClose={onClose}
      actions={
        <>
          <Button variant="primary" size="lg" onClick={save} disabled={create.isPending || update.isPending}>
            Zapisz
          </Button>
          <Button variant="secondary" size="lg" onClick={onClose}>
            Anuluj
          </Button>
        </>
      }
    >
      <TextInput label="Nazwa" value={name} onChange={(e) => setName(e.target.value)} error={error} />
      <Switch
        label="Usługa okresowa"
        description="Po wykonaniu zaproponuj termin kolejnej wizyty."
        checked={isPeriodic}
        onChange={setPeriodic}
      />
      {isPeriodic && (
        <SelectInput
          label="Kolejna wizyta"
          value={String(periodMonths)}
          options={PERIOD_OPTIONS.some((o) => o.value === String(periodMonths))
            ? PERIOD_OPTIONS
            : [...PERIOD_OPTIONS, { value: String(periodMonths), label: `co ${periodMonths} miesięcy` }]}
          onChange={(e) => setPeriod(Number(e.target.value))}
        />
      )}
      {type && (
        <Switch
          label="Pokazuj przy nowych wizytach"
          description="Ukryty typ zostaje w starych wizytach."
          checked={isActive}
          onChange={setActive}
        />
      )}
    </Dialog>
  );
}
